import React, { useState } from 'react';
import { 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Typography,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import axiosInstance from '../../utils/axiosConfig';

interface PaymentSyncComponentProps {
  competitionId: string;
  onSyncComplete?: () => void;
}

interface SyncResult {
  message: string;
  updatedCount: number;
  errorCount: number;
  errors?: Array<{
    confirmationId: string;
    clubId: string;
    error: string;
  }>;
}

const PaymentSyncComponent: React.FC<PaymentSyncComponentProps> = ({ 
  competitionId, 
  onSyncComplete 
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SyncResult | null>(null);

  const handleSync = async () => {
    setLoading(true);
    setResult(null);
    
    try {
      const response = await axiosInstance.post(`/api/payments/sync-payments/${competitionId}`);
      setResult(response.data);
      
      if (onSyncComplete) {
        onSyncComplete();
      }
    } catch (error: any) {
      setResult({
        message: 'Fehler beim Synchronisieren der Zahlungen',
        updatedCount: 0,
        errorCount: 1,
        errors: [{ 
          confirmationId: '', 
          clubId: '', 
          error: error.response?.data?.message || 'Unbekannter Fehler' 
        }]
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setResult(null);
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<SyncIcon />}
        onClick={() => setOpen(true)}
        size="small"
      >
        Zahlungen synchronisieren
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Zahlungsstatus synchronisieren
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Diese Funktion überprüft alle Zahlungen gegen die aktuellen Meldungen und 
              aktualisiert den Zahlungsstatus entsprechend. Dies ist nützlich, wenn sich 
              Meldungen geändert haben, nachdem Zahlungen erfasst wurden.
            </Typography>
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 3 }}>
              <CircularProgress size={24} />
              <Typography>Synchronisiere Zahlungen...</Typography>
            </Box>
          )}

          {result && (
            <Box sx={{ mt: 2 }}>
              <Alert 
                severity={result.errorCount > 0 ? 'warning' : 'success'}
                sx={{ mb: 2 }}
              >
                {result.message}
              </Alert>

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Chip 
                  label={`${result.updatedCount} aktualisiert`} 
                  color="success" 
                  variant="outlined" 
                />
                {result.errorCount > 0 && (
                  <Chip 
                    label={`${result.errorCount} Fehler`} 
                    color="error" 
                    variant="outlined" 
                  />
                )}
              </Box>

              {result.errors && result.errors.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Fehler bei der Synchronisation:
                  </Typography>
                  <List dense>
                    {result.errors.map((error, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={`Club: ${error.clubId || 'Unbekannt'}`}
                          secondary={error.error}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Schließen
          </Button>
          {!result && (
            <Button 
              onClick={handleSync} 
              color="primary" 
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Synchronisiere...' : 'Jetzt synchronisieren'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PaymentSyncComponent;